import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../config/axiosConfig';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDateRange = (period) => {
    const now = new Date();
    const start = new Date();

    switch (period) {
        case 'mois':
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            break;
        case 'annee':
        default:
            start.setMonth(0, 1);
            start.setHours(0, 0, 0, 0);
            break;
    }

    return {
        startDate: start.toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0],
    };
};

const getPrevDateRange = (period) => {
    const now = new Date();
    const start = new Date();
    const end = new Date();

    if (period === 'mois') {
        // Mois précédent
        start.setMonth(now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(now.getMonth(), 0); // dernier jour du mois précédent
        end.setHours(23, 59, 59, 999);
    } else {
        // Année précédente
        start.setFullYear(now.getFullYear() - 1, 0, 1);
        start.setHours(0, 0, 0, 0);
        end.setFullYear(now.getFullYear() - 1, 11, 31);
        end.setHours(23, 59, 59, 999);
    }

    return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
    };
};

// ─── Transforme la réponse brute de l'API ─────────────────────────────────────

const transformApiResponse = (data = {}) => {
    const totalPaiements =
        (data.totalPaiementCircuit || 0) +
        (data.totalPaiementExcursion || 0) +
        (data.totalPaiementSejour || 0) +
        (data.totalPaiementTransfert || 0) +
        (data.totalPaiementBillet || 0);

    return {
        // KPIs
        totalDossiers: (data.totalCircuits || 0) + (data.totalBillets || 0)
            + (data.totalExcursions || 0) + (data.totalSejours || 0) + (data.totalTransferts || 0),
        totalClients: data.totalClients || data.paiementByClient?.length || 0,
        totalBillets: data.totalBillets || 0,
        totalCircuits: data.totalCircuits || 0,
        totalExcursions: data.totalExcursions || 0,
        totalSejours: data.totalSejours || 0,
        chiffreAffaires: totalPaiements,

        // Répartition par activité (pour PieChart)
        // Utiliser les comptes (counts) au lieu du CA pour refléter les "services actifs"
        repartitionActivite: [
            { category: 'Circuits', value: data.totalCircuits || 0 },
            { category: 'Billets', value: data.totalBillets || 0 },
            { category: 'Excursions', value: data.totalExcursions || 0 },
            { category: 'Séjours', value: data.totalSejours || 0 },
            { category: 'Transferts', value: data.totalTransferts || 0 },
        ].filter(d => d.value > 0),

        // Ventes par mois (si fourni par l'API)
        ventesParMois: data.ventesParMois || [],

        // Clients top (si fourni)
        topClients: data.paiementByClient?.slice(0, 5) || [],

        // Dossiers récents circuits (si fourni)
        recentCircuits: data.recentCircuits || [],

        // Dossiers récents billets (si fourni)
        recentBillets: data.recentBillets || [],

        // Données brutes pour calcul mensuel côté front si API ne fournit pas
        _raw: data,
    };
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

/** Charge les stats pour la période actuelle */
export const fetchStatistiques = createAsyncThunk(
    'statistiques/fetchCurrent',
    async (timeRange = 'annee', { rejectWithValue }) => {
        try {
            const { startDate, endDate } = getDateRange(timeRange);
            const response = await api.get('/dashboard', {
                params: { startDate, endDate, cloture_definitive: 1 },
            });
            return { data: transformApiResponse(response.data), timeRange };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/** Charge les stats pour la période précédente (pour les tendances) */
export const fetchStatistiquesPrev = createAsyncThunk(
    'statistiques/fetchPrev',
    async (timeRange = 'annee', { rejectWithValue }) => {
        try {
            const { startDate, endDate } = getPrevDateRange(timeRange);
            const response = await api.get('/dashboard', {
                params: { startDate, endDate, cloture_definitive: 1 },
            });
            return transformApiResponse(response.data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/** Charge les ventes mensuelles pour l'année complète (bar chart) */
export const fetchVentesAnnuelles = createAsyncThunk(
    'statistiques/fetchVentesAnnuelles',
    async (_, { rejectWithValue }) => {
        try {
            const year = new Date().getFullYear();
            const response = await api.get('/dashboard', {
                params: {
                    startDate: `${year}-01-01`,
                    endDate: `${year}-12-31`,
                    cloture_definitive: 1,
                },
            });
            const data = response.data;

            if (data.ventesParMois && Array.isArray(data.ventesParMois)) {
                // Si l'API retourne déjà le format {category, value}, on le retourne tel quel
                if (data.ventesParMois.length > 0 && typeof data.ventesParMois[0] === 'object') {
                    return data.ventesParMois;
                }
                
                // Sinon on mappe les noms de mois sur les valeurs
                const monthsList = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                return monthsList.map((label, i) => ({
                    category: label,
                    value: data.ventesParMois[i] || 0,
                }));
            }

            // Fallback : construire depuis les champs de totaux
            // Dans ce cas, on retourne un seul point (total de l'année)
            const total = (data.totalPaiementCircuit || 0)
                + (data.totalPaiementBillet || 0)
                + (data.totalPaiementExcursion || 0)
                + (data.totalPaiementSejour || 0)
                + (data.totalPaiementTransfert || 0);

            const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
            const currentMonth = new Date().getMonth();
            return months.map((label, i) => ({
                category: label,
                value: i === currentMonth ? total : 0,
            }));
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const statistiquesSlice = createSlice({
    name: 'statistiques',
    initialState: {
        current: null,
        prev: null,
        ventesParMois: [],
        loading: false,
        loadingPrev: false,
        loadingVentes: false,
        error: null,
        timeRange: 'annee',
        lastUpdated: null,
    },
    reducers: {
        setTimeRange: (state, action) => {
            state.timeRange = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Current period
        builder
            .addCase(fetchStatistiques.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStatistiques.fulfilled, (state, action) => {
                state.loading = false;
                state.current = action.payload.data;
                state.timeRange = action.payload.timeRange;
                state.lastUpdated = new Date().toISOString();
            })
            .addCase(fetchStatistiques.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Erreur de chargement';
            });

        // Previous period
        builder
            .addCase(fetchStatistiquesPrev.pending, (state) => {
                state.loadingPrev = true;
            })
            .addCase(fetchStatistiquesPrev.fulfilled, (state, action) => {
                state.loadingPrev = false;
                state.prev = action.payload;
            })
            .addCase(fetchStatistiquesPrev.rejected, (state) => {
                state.loadingPrev = false;
            });

        // Ventes annuelles
        builder
            .addCase(fetchVentesAnnuelles.pending, (state) => {
                state.loadingVentes = true;
            })
            .addCase(fetchVentesAnnuelles.fulfilled, (state, action) => {
                state.loadingVentes = false;
                state.ventesParMois = action.payload;
            })
            .addCase(fetchVentesAnnuelles.rejected, (state) => {
                state.loadingVentes = false;
            });
    },
});

export const { setTimeRange, clearError } = statistiquesSlice.actions;
export default statistiquesSlice;
